//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

//  emit WithdrawRequested(owner, assets, shares);
import * as yup from 'yup'
import { InferType } from 'yup'
import { ChainId, yupEthAddrOptional, yupEthAddrRequired } from './Enums'
import { ArrayTypeEnum, EthAddr, ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

export const OPEN_TRADE_UUID = '12345678-1234-4234-a234-123456789012'
export const ALL_UUID = '11111111-2222-4333-a444-555555555555'

export enum CompanyProfileStatusEnum {
  VERIFIED = 'VERIFIED',
  SELF_REGISTERED = 'SELF_REGISTERED',
  UNSIGNED = 'UNSIGNED',
  BLOCKED = 'BLOCKED',
}

export enum VaultAccessStatusEnum {
  UNASSIGNED = 'UNASSIGNED',
  ALLOWED = 'ALLOWED',
  DENIED = 'DENIED',
  REQUESTED = 'REQUESTED',
  REMOVE = 'REMOVE',
}
export enum CompanyProfileActionEnum {
  InitializeCompanyProfile = 'InitializeCompanyProfile',
  SELF_REGISTRATION = 'SELF_REGISTRATION',
  ADD_EMAIL = 'ADD_EMAIL',
  REMOVE_EMAIL = 'REMOVE_EMAIL',
  DELETE_PROFILE = 'DELETE_PROFILE',
  REQUEST_ACCESS = 'REQUEST_ACCESS',
  REMOVE_ACCESS = 'REMOVE_ACCESS',
  ADD_REFERRAL_CODE = 'ADD_REFERRAL_CODE',
  REMOVE_REFERRAL_CODE = 'REMOVE_REFERRAL_CODE',
  ADD_WALLET = 'ADD_WALLET',
  REMOVE_WALLET = 'REMOVE_WALLET',
}

export const PoolAccessControlSchema = yup.object({
  id: yup
    .string()
    .label('Vault Address')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'id',
        description: 'poolAddr-walletAddr',
      })
    ),
  poolAddr: yupEthAddrRequired
    .label('Vault Address')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'poolAddr',
        description: 'The smart-contract address of the loan vault.',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  walletAddr: yupEthAddrRequired
    .label('Wallet Address')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'walletAddr',
        description: 'Wallet',
        stringType: StringTypeEnum.ETH_ADDR,
      })
    ),
  chainId: yup
    .mixed<ChainId>()
    .oneOf(Object.values(ChainId) as unknown as ChainId[])
    .defined()
    .label('Chain Id')
    .meta(toFieldMetaData({ propName: 'chainId', description: 'ChainId of the transaction' })),

  symbol: yup
    .string()
    .required()
    .label('symbol ')
    .meta(
      toFieldMetaData({
        propName: 'symbol',
        description: 'symbol if it is a ERC20 token',
      })
    ),
  poolName: yup
    .string()
    .required()
    .label('poolName ')
    .meta(
      toFieldMetaData({
        propName: 'poolName',
        description: 'poolName ',
      })
    ),
  displayName: yup
    .string()
    .required()
    .label('displayName')
    .meta(
      toFieldMetaData({
        propName: 'displayName',
        description: 'displayName',
      })
    ),
  status: yup
    .mixed<VaultAccessStatusEnum>()
    .oneOf(Object.values(VaultAccessStatusEnum))
    .default(VaultAccessStatusEnum.UNASSIGNED)
    .label('status')
    .meta(
      toFieldMetaData({
        propName: 'status',
        choices: Object.keys(VaultAccessStatusEnum),
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 6,
      })
    ),
})

export const CompanyProfileActionCTOSchema = yup.object({
  uuid: yup
    .string()
    .uuid()
    .optional()
    .label('Company Profile Id')
    .meta(toFieldMetaData({ propName: 'uuid', stringType: StringTypeEnum.ID, description: 'ID of the company' })),

  action: yup
    .mixed<CompanyProfileActionEnum>()
    .oneOf(Object.values(CompanyProfileActionEnum))
    .required()
    .label('action')
    .meta(
      toFieldMetaData({
        propName: 'roleEnum',
        choices: Object.keys(CompanyProfileActionEnum),
        description: `Action of the company profile, Valid actions are
        ADD_REFERRAL ,  SELF_REGISTRATION ,  ADD_EMAIL,  REMOVE_EMAIL,
      DELETE_PROFILE ,  REQUEST_ACCESS ,  REMOVE_ACCESS ,  ADD_REFERRAL_CODE ,
      REMOVE_REFERRAL_CODE ,  ADD_WALLET ,  REMOVE_WALLET`,
      })
    ),
  originatorUuid: yup
    .string()
    .uuid()
    .optional()
    .label('Originator Id')
    .meta(
      toFieldMetaData({
        propName: 'uuid',
        stringType: StringTypeEnum.ID,
        description:
          "ID of the originator this can be the originator's UUID or any one of the Referral codes for the originator",
      })
    ),

  companyName: yup
    .string()
    .optional()
    .label('Company Name')
    .meta(toFieldMetaData({ propName: 'companyName', description: 'Name of the company' })),
  wallets: yup
    .array()
    .of(
      yupEthAddrRequired
        .label('wallet')
        .meta(toFieldMetaData({ propName: 'wallet', stringType: StringTypeEnum.ETH_ADDR }))
    )
    .optional()
    .label('Wallets')
    .meta(toFieldMetaData({ propName: 'wallets', description: 'Wallets of the company' })),

  emails: yup
    .array()
    .of(
      yup
        .string()
        .email()
        .required()
        .label('Email')
        .meta(toFieldMetaData({ propName: 'email' }))
    )
    .optional()
    .label('Wallets')
    .meta(toFieldMetaData({ propName: 'emails' })),
  instructions: yup
    .string()
    .optional()
    .label('Instructions')
    .meta(toFieldMetaData({ propName: 'instructions' })),
  requestedPoolsAccess: yup
    .array()
    .of(
      yupEthAddrRequired
        .label('wallet')
        .meta(toFieldMetaData({ propName: 'requestedPoolsAccess', stringType: StringTypeEnum.ETH_ADDR }))
    )
    .optional()
    .label('Wallets')
    .meta(toFieldMetaData({ propName: 'requestedPoolsAccess', description: 'Requested Pools Access' })),
})

export const CompanyProfileCTOSchema = yup.object({
  uuid: yup
    .string()
    .uuid()
    .required()
    .label('Company Profile Id')
    .meta(
      toFieldMetaData({
        propName: 'uuid',
        stringType: StringTypeEnum.ID,
        description: 'ID of the company',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 3,
        columnWidth: 300,
      })
    ),
  isIssuer: yup
    .boolean()
    .optional()

    .label('Is Issuer')
    .meta(toFieldMetaData({ propName: 'isIssuer', description: 'Whether the company is an issuer' })),
  isOriginator: yup
    .boolean()
    .optional()
    .label('Is Originator')
    .meta(toFieldMetaData({ propName: 'isOriginator', description: 'Whether the company is an originator' })),
  originatorUuid: yup
    .string()
    .uuid()
    .optional()
    .label('Originator Id')
    .meta(
      toFieldMetaData({
        propName: 'originatorUuid',
        stringType: StringTypeEnum.ID,
        description: 'ID of the originator',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 8,
        columnWidth: 250,
      })
    ),
  originatorAccessCodeUsed: yup
    .string()
    .uuid()
    .optional()
    .label('Originator Code used in ')
    .meta(
      toFieldMetaData({
        propName: 'originatorAccessCodeUsed',
        stringType: StringTypeEnum.ID,
        description: 'ID of the originator',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 8,
        columnWidth: 250,
      })
    ),

  issuerVaultAddr: yupEthAddrOptional
    .label('Issuer Vault Address')
    .meta(toFieldMetaData({ propName: 'issuerVaultAddr', description: 'Address of the issuer vault' })),

  companyName: yup
    .string()
    .required()
    .label('Company Name')
    .meta(
      toFieldMetaData({
        propName: 'companyName',
        description: 'Name of the company',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 2,
      })
    ),

  wallets: yup
    .array()
    .of(
      yupEthAddrRequired
        .label('wallet')
        .meta(toFieldMetaData({ propName: 'wallet', stringType: StringTypeEnum.ETH_ADDR }))
    )
    .required()
    .label('Wallets')
    .meta(
      toFieldMetaData({
        propName: 'wallets',
        description: 'Wallets of the company',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 4,
        columnWidth: 250,
      })
    ),

  emails: yup
    .array()
    .of(
      yup
        .string()
        .email()
        .required()
        .label('Email')
        .meta(
          toFieldMetaData({
            propName: 'emails',
          })
        )
    )
    .required()
    .label('Emails')
    .meta(
      toFieldMetaData({
        propName: 'emails',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 5,
        columnWidth: 250,
      })
    ),

  status: yup
    .mixed<CompanyProfileStatusEnum>()
    .oneOf(Object.values(CompanyProfileStatusEnum))
    .optional()
    .label('status')
    .meta(
      toFieldMetaData({
        propName: 'status',
        choices: Object.keys(CompanyProfileStatusEnum),
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 6,
      })
    ),
  referralCreationAccessCode: yup
    .string()
    .optional()
    .label('Referral Creation Access Code')
    .meta(
      toFieldMetaData({
        propName: 'referralCreationAccessCode',
        stringType: StringTypeEnum.ID,
        description: 'Referral Creation Access Code',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 8,
        columnWidth: 300,
      })
    ),

  instructions: yup
    .string()
    .optional()
    .label('Instructions')
    .meta(
      toFieldMetaData({
        propName: 'instructions',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 7,
      })
    ),

  poolAccessControls: yup
    .array()
    .of(PoolAccessControlSchema)
    .optional()
    .label('Pools Access Controls')
    .meta(
      toFieldMetaData({
        arrayType: ArrayTypeEnum.POOL_ACCESS_CONTROL,
        propName: 'poolAccessControls',
        description: 'Pools Access Controls',
      })
    ),
})
export interface PoolAccessControlCTO {
  id: string
  walletAddr: EthAddr
  poolAddr: EthAddr
  chainId: ChainId
  symbol: string
  poolName: string
  displayName: string
  status: VaultAccessStatusEnum
}

type PoolAccessControlCTOInfer = InferType<typeof PoolAccessControlSchema>
export const _testACompanyAccess: PoolAccessControlCTO = {} as unknown as PoolAccessControlCTOInfer
export const _testBCompanyAccess: PoolAccessControlCTOInfer = {} as unknown as PoolAccessControlCTO

type CompanyProfileCTOSInfer = InferType<typeof CompanyProfileCTOSchema>
export const _testACompanyProfile: CompanyProfileCTO = {} as unknown as CompanyProfileCTOSInfer
export const _testBCompanyProfile: CompanyProfileCTOSInfer = {} as unknown as CompanyProfileCTO

export interface CompanyProfileCTO {
  uuid: string
  isIssuer?: boolean
  isOriginator?: boolean
  originatorUuid?: string
  originatorAccessCodeUsed?: string
  referralCreationAccessCode?: string
  issuerVaultAddr?: EthAddr
  wallets: EthAddr[]
  emails: string[]
  companyName: string
  instructions?: string
  status?: CompanyProfileStatusEnum

  poolAccessControls?: PoolAccessControlCTO[]
}

export interface CompanyProfileActionCTO {
  uuid?: string
  originatorUuid?: string
  action: NonNullable<CompanyProfileActionEnum>
  companyName?: string
  wallets?: EthAddr[]
  emails?: string[]
  instructions?: string
  requestedPoolsAccess?: EthAddr[]
  referralCreationAccessCode?: string
}
type CompanyProfileActionCTOInfer = InferType<typeof CompanyProfileActionCTOSchema>
export const _testACompanyProfileAction: CompanyProfileActionCTO = {} as unknown as CompanyProfileActionCTOInfer
export const _testBCompanyProfileAction: CompanyProfileActionCTOInfer = {} as unknown as CompanyProfileActionCTO
